import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiZap,
  FiEye,
  FiPlay,
  FiPause,
  FiRefreshCw,
  FiHome,
} from 'react-icons/fi';
import '../styles/LessonStyles.css';
import GetStartedWith from '../../../../components/GetStartedWith';

const chunkingTechniques = [
  {
    id: 1,
    technique: 'Basic Word Chunking',
    description: 'Group 3-5 words together instead of reading word by word.',
    example: {
      wordByWord: [
        'The',
        'rapid',
        'development',
        'of',
        'artificial',
        'intelligence',
        'has',
        'revolutionized',
        'many',
        'industries',
      ],
      chunked: [
        'The rapid development',
        'of artificial intelligence',
        'has revolutionized',
        'many industries',
      ],
    },
    benefits: [
      'Increases reading speed by 40-60%',
      'Reduces eye strain',
      'Improves comprehension flow',
    ],
  },
  {
    id: 2,
    technique: 'Phrase-Based Chunking',
    description:
      'Identify natural phrase boundaries and read in meaningful units.',
    example: {
      wordByWord: [
        'Scientists',
        'have',
        'recently',
        'discovered',
        'that',
        'climate',
        'change',
        'is',
        'occurring',
        'faster',
        'than',
        'expected',
      ],
      chunked: [
        'Scientists have recently discovered',
        'that climate change',
        'is occurring faster than expected',
      ],
    },
    benefits: [
      'Maintains meaning while reading faster',
      'Easier to remember information',
      'Natural reading rhythm',
    ],
  },
  {
    id: 3,
    technique: 'Skip Non-Essential Modifiers',
    description:
      'Focus on core subject-verb-object and skip descriptive modifiers on first read.',
    example: {
      original:
        'The extremely complex and highly sophisticated mathematical algorithms, developed over many years by brilliant researchers, have finally solved the problem.',
      coreReading: 'The algorithms have solved the problem.',
      modifiers: [
        'extremely complex',
        'highly sophisticated',
        'mathematical',
        'developed over many years',
        'by brilliant researchers',
        'finally',
      ],
    },
    benefits: [
      'Dramatically increases speed',
      'Focuses on main ideas',
      'Can return to details if needed',
    ],
  },
];

const practiceExercises = [
  {
    id: 1,
    title: 'Basic Chunking Practice',
    instruction:
      'Practice reading the following sentences in chunks of 3-4 words. Click on each chunk as you read it.',
    sentences: [
      {
        text: 'The recent breakthrough in quantum computing represents a significant advancement in technology.',
        chunks: [
          'The recent breakthrough',
          'in quantum computing',
          'represents a significant',
          'advancement in technology.',
        ],
      },
      {
        text: 'Environmental scientists are increasingly concerned about the accelerating rate of climate change.',
        chunks: [
          'Environmental scientists are',
          'increasingly concerned about',
          'the accelerating rate',
          'of climate change.',
        ],
      },
    ],
  },
  {
    id: 2,
    title: 'Speed Reading Test',
    instruction:
      'Read the following passage using chunking techniques. Try to maintain comprehension while reading as quickly as possible.',
    passage: `Modern technology has transformed the way we communicate, work, and live our daily lives. From smartphones that connect us instantly to people around the world, to artificial intelligence systems that can process vast amounts of information in seconds, our technological capabilities continue to expand at an unprecedented rate.

However, this rapid technological advancement also brings significant challenges. Privacy concerns, job displacement due to automation, and the digital divide between those who have access to technology and those who do not are all pressing issues that society must address.

Despite these challenges, the potential benefits of continued technological development are enormous. Medical breakthroughs, sustainable energy solutions, and educational innovations all promise to improve quality of life for people around the globe.`,
    questions: [
      {
        question: 'What is the main point of this passage?',
        options: [
          'Technology is entirely beneficial to society',
          'Technology presents both opportunities and challenges',
          'Technology should be avoided due to its risks',
          'Technology only benefits wealthy people',
        ],
        correct: 1,
      },
    ],
  },
];

export default function ChunkReadingLesson() {
  const navigate = useNavigate();
  const practiceRef = useRef(null);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [clickedChunks, setClickedChunks] = useState([]);
  const [readingStartTime, setReadingStartTime] = useState(null);
  const [readingEndTime, setReadingEndTime] = useState(null);
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const scrollToPractice = () => {
    practiceRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChunkClick = (chunkIndex) => {
    if (!clickedChunks.includes(chunkIndex)) {
      setClickedChunks((prev) => [...prev, chunkIndex]);
    }
  };

  const startSpeedReading = () => {
    setReadingStartTime(Date.now());
    setReadingEndTime(null);
    setShowResults(false);
  };

  const finishSpeedReading = () => {
    setReadingEndTime(Date.now());
  };

  const calculateWPM = () => {
    if (!readingStartTime || !readingEndTime) return 0;
    const timeInMinutes = (readingEndTime - readingStartTime) / 60000;
    const wordCount = practiceExercises[1].passage.split(' ').length;
    return Math.round(wordCount / timeInMinutes);
  };

  const resetExercise = () => {
    setClickedChunks([]);
    setCurrentExercise(0);
    setReadingStartTime(null);
    setReadingEndTime(null);
    setPracticeAnswers({});
    setShowResults(false);
  };

  const renderOverview = () => (
    <div className="lesson-content">
      <h2 className="section-title">Master Chunk Reading Techniques</h2>

      <div className="lesson-intro">
        <p>
          Chunk reading is the most effective technique for increasing reading
          speed while maintaining comprehension. Instead of reading
          word-by-word, you'll learn to process groups of words as single units
          of meaning.
        </p>
      </div>

      <div className="question-types-grid">
        {chunkingTechniques.map((technique) => (
          <div
            key={technique.id}
            onClick={() => setSelectedTechnique(technique)}
            className={`question-type-card ${selectedTechnique?.id === technique.id ? 'selected' : ''}`}
          >
            <div className="type-header">
              <div className="type-number">
                <FiZap />
              </div>
              <h3 className="type-title">{technique.technique}</h3>
            </div>
            <p className="type-description">{technique.description}</p>
          </div>
        ))}
      </div>

      {selectedTechnique && (
        <div className="technique-details">
          <h3 className="technique-title">
            <FiEye />
            {selectedTechnique.technique}
          </h3>

          <div className="example-comparison">
            <h4 className="comparison-title">Example Comparison:</h4>

            {selectedTechnique.id === 3 ? (
              <div>
                <div className="comparison-item">
                  <h5 className="comparison-subtitle error">
                    Original (with all modifiers):
                  </h5>
                  <div className="comparison-text error">
                    {selectedTechnique.example.original}
                  </div>
                </div>

                <div className="comparison-item">
                  <h5 className="comparison-subtitle success">
                    Core reading (skip modifiers):
                  </h5>
                  <div className="comparison-text success">
                    {selectedTechnique.example.coreReading}
                  </div>
                </div>

                <div>
                  <h5 className="comparison-subtitle">Skipped modifiers:</h5>
                  <div className="comparison-text">
                    {selectedTechnique.example.modifiers.map(
                      (modifier, index) => (
                        <span key={index} className="modifier">
                          {modifier}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="comparison-item">
                  <h5 className="comparison-subtitle error">
                    Word-by-word reading:
                  </h5>
                  <div className="comparison-text error">
                    {selectedTechnique.example.wordByWord.map((word, index) => (
                      <span key={index} className="word">
                        {word}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="comparison-subtitle success">
                    Chunked reading:
                  </h5>
                  <div className="comparison-text success">
                    {selectedTechnique.example.chunked.map((chunk, index) => (
                      <span key={index} className="chunk">
                        {chunk}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="benefits">
            <h4 className="benefits-title">Benefits:</h4>
            <ul>
              {selectedTechnique.benefits.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  const renderPractice = () => (
    <div ref={practiceRef} className="practice-section">
      <h2 className="section-title">Practice Exercises</h2>

      <div className="practice-actions">
        <div>
          <button
            onClick={() => setCurrentExercise(0)}
            className={`practice-button ${currentExercise === 0 ? 'selected' : ''}`}
          >
            Chunking Practice
          </button>
          <button
            onClick={() => setCurrentExercise(1)}
            className={`practice-button ${currentExercise === 1 ? 'selected' : ''}`}
          >
            Speed Reading Test
          </button>
        </div>
        <button onClick={resetExercise} className="reset-button">
          <FiRefreshCw /> Reset
        </button>
      </div>

      {currentExercise === 0 && (
        <div className="practice-item">
          <h3 className="item-title">{practiceExercises[0].title}</h3>
          <p className="item-description">{practiceExercises[0].instruction}</p>

          {practiceExercises[0].sentences.map((sentence, sentenceIndex) => (
            <div key={sentenceIndex} className="sentence-item">
              <h4 className="sentence-title">Sentence {sentenceIndex + 1}:</h4>
              <div className="sentence-chunks">
                {sentence.chunks.map((chunk, chunkIndex) => (
                  <button
                    key={chunkIndex}
                    onClick={() =>
                      handleChunkClick(`${sentenceIndex}-${chunkIndex}`)
                    }
                    className={`chunk-button ${clickedChunks.includes(`${sentenceIndex}-${chunkIndex}`) ? 'selected' : ''}`}
                  >
                    {chunk}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {currentExercise === 1 && (
        <div className="practice-item">
          <h3 className="item-title">{practiceExercises[1].title}</h3>
          <p className="item-description">{practiceExercises[1].instruction}</p>

          <div className="reading-controls">
            {!readingStartTime ? (
              <button onClick={startSpeedReading} className="start-button">
                <FiPlay /> Start Speed Reading
              </button>
            ) : !readingEndTime ? (
              <button onClick={finishSpeedReading} className="stop-button">
                <FiPause /> Finish Reading
              </button>
            ) : (
              <div className="reading-result">
                Your reading speed: {calculateWPM()} WPM
                {calculateWPM() >= 500 && ' 🎉 Excellent speed!'}
                {calculateWPM() >= 400 &&
                  calculateWPM() < 500 &&
                  ' 👍 Good speed!'}
                {calculateWPM() < 400 &&
                  ' Keep practicing to reach 500 WPM target!'}
              </div>
            )}
          </div>

          <div className="passage-content">
            {practiceExercises[1].passage
              .split('\n\n')
              .map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
          </div>

          {readingEndTime && (
            <div className="comprehension-check">
              <h4 className="check-title">Comprehension Check:</h4>
              {practiceExercises[1].questions.map((question, qIndex) => (
                <div key={qIndex} className="question-item">
                  <p className="question-text">{question.question}</p>
                  <div className="options-container">
                    {question.options.map((option, optionIndex) => (
                      <button
                        key={optionIndex}
                        onClick={() =>
                          setPracticeAnswers((prev) => ({
                            ...prev,
                            [qIndex]: optionIndex,
                          }))
                        }
                        className={`option-button ${practiceAnswers[qIndex] === optionIndex ? 'selected' : ''}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  {practiceAnswers[qIndex] !== undefined && (
                    <div
                      className={`result-indicator ${practiceAnswers[qIndex] === question.correct ? 'correct' : 'incorrect'}`}
                    >
                      {practiceAnswers[qIndex] === question.correct
                        ? '✓ Correct! You maintained good comprehension while speed reading.'
                        : '✗ Incorrect. Try reading slightly slower to maintain comprehension.'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="lesson-container">
      {/* Header */}
      <div className="lesson-header">
        <button
          onClick={() =>
            navigate('/exams/gre/', { state: { activeCategory: 'reading' } })
          }
          className="back-button"
        >
          <FiArrowLeft /> Back to Reading Comprehension
        </button>

        <div className="header-actions">
          <button onClick={scrollToPractice} className="practice-button">
            Practice
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="lesson-title-section">
        <h1 className="lesson-title">
          <FiZap />
          Lesson 4: Chunk Reading
        </h1>
        <p className="lesson-subtitle">
          Master the art of reading in chunks to dramatically increase your
          reading speed while maintaining comprehension. This technique is
          essential for managing GRE time constraints.
        </p>
      </div>

      {/* Content */}
      {renderOverview()}
      {renderPractice()}

      {/* Get Started With Component */}
      <div style={{ margin: '3rem 0 2rem 0' }}>
        <GetStartedWith
          title="Need help with Chunk Reading techniques?"
          spaceName="Verbal vault"
          humanCoachTitle="Talk to a GRE coach"
          liwazaAITitle="Ask AI"
          communityTitle="Ask other students"
          showHumanCoach={true}
          showLiwazaAI={true}
          showCommunity={true}
          aifirstmessage="I need help understanding Chunk Reading techniques for GRE Reading Comprehension"
          aicontext="Chunk reading is the most effective technique for increasing reading speed while maintaining comprehension. Instead of reading word-by-word, you'll learn to process groups of words as single units of meaning."
        />
      </div>

      {/* Navigation */}
      <div className="lesson-navigation">
        <button
          onClick={() =>
            navigate(
              '/exams/gre/reading-comprehension/lesson/passage-components'
            )
          }
          className="prev-lesson-button"
        >
          <FiArrowLeft /> Previous: Passage Components
        </button>

        <button
          onClick={() =>
            navigate('/exams/gre/reading-comprehension/lesson/speed-reading')
          }
          className="next-lesson-button"
        >
          Next: Target 500 WPM <FiArrowRight />
        </button>
      </div>
    </div>
  );
}
